/**
 * The slice of `content/game.config.json` the renderer needs, parsed once at boot.
 *
 * Why a hand-written parser instead of ajv: this runs before the first frame and
 * it is on the 6 s time-to-play budget, so the boot path does not pull a JSON
 * Schema validator into the initial chunk. `make validate-content` already checks
 * the file against `content/schemas/game.config.schema.json` in CI; this parser
 * is the runtime guard that turns "the config is wrong" into a `Result` the
 * composition root can report instead of a black canvas.
 *
 * Why the types below are `Pick`s of the port and not interfaces of their own:
 * the hand parse is a *reading* of `content/game.config.json`, not a second
 * declaration of it. `app/application/ports` already mirrors
 * `content/schemas/game.config.schema.json` property for property, and
 * `tests/unit/contracts/ports-match-schemas.test.ts` is the gate that keeps it
 * mirroring. Deriving from `GameConfigDocument` puts this adapter behind that
 * same gate: rename a property in the schema and the port test fails, then the
 * port is fixed and this file stops compiling. Restating the shape here is how
 * `levelOrder` survived a slice while the config said `levels` — the second
 * declaration was invisible from the first. The import is type-only, so the
 * derivation costs nothing at runtime and no application code, and no validator,
 * reaches the boot path (`boot-config-derives-from-port.test.ts` checks both).
 *
 * Adapters may import `common/` and the application's ports — they exist to
 * implement them — but not `app/ui`, `app/bootstrap` or another adapter (ADR-0005).
 */

import type {
  FeatureFlags,
  GameConfigDocument,
  GraphicsPreset,
  GraphicsPresets,
  PerformanceBudgets,
  ThemeColours,
} from '@application/ports';

import { appErr, ok, type Result } from '@common/result';

/**
 * The renderer's view of the config: the handful of properties it needs, under
 * the port's types.
 *
 * `ThemeColours` is the palette (`game.config.schema.json#/$defs/theme`) — the
 * sky at the top of the playfield and the ground at the bottom paint the canvas
 * gradient *and* the page background behind it, which is how the desktop side
 * panels are made (see `GameRenderer`).
 *
 * Only two members are the renderer's own; everything else is `Pick`ed so that
 * the name here is the name in the schema, checked by the compiler.
 */
export interface BootConfig
  extends Pick<
      GameConfigDocument,
      | 'title'
      | 'version'
      | 'defaultLocale'
      | 'designWidth'
      | 'designHeight'
      /* The three visual tiers the renderer probe chooses between (task 1.19).
         Read from the config rather than declared in the adapter so re-tuning a
         tier is a content edit, and so the tiers cannot drift from the budgets
         they were written against. */
      | 'graphicsPresets'
      /* The locomotion vocabulary a level may name (ADR-0023). Read here so the
         level parser can be handed it rather than holding a second copy — the
         copy that let `toboggan` pass `validate-content` and fail at load. */
      | 'locomotionModes'
    >,
    /* Flattened out of `featureFlags`: the scene needs the flag, not the block. */
    Pick<FeatureFlags, 'debugOverlay'>,
    /* Flattened out of `budgets` for the same reason: the tier thresholds are
       ratios of this one number (see `visual-tier.ts`), and nothing on the boot
       path needs the payload or texture budgets. */
    Pick<PerformanceBudgets, 'frameTimeMs'> {
  /** Phaser's clear colour, `#rrggbb`. Equal to `palette.sky`; not a config property. */
  readonly backgroundColor: string;
  /**
   * The config's `theme` after the merge below. Optional in the document,
   * always complete here, so the scene can draw without a fallback of its own.
   */
  readonly palette: ThemeColours;
}

/**
 * The palette used when the document being parsed supplies none.
 *
 * `theme` is optional in `content/schemas/game.config.schema.json` and optional
 * again per level, because most levels differ from the game default in one or
 * two colours and a level should not have to restate the whole block to change
 * its sky. So every key here is a per-key fallback: `readPalette` merges over
 * this object, and a document that omits `theme` entirely — or omits `inkMuted`
 * inside it — still yields a complete, drawable palette rather than a black
 * canvas or an `undefined` colour reaching Phaser.
 *
 * These are therefore the renderer's own last-resort values, not a copy of the
 * shipped config and not the source of the colours you see: `content/game.config.json`
 * carries a `theme` and it wins (see `boot-config.test.ts`, "lets the shipped
 * theme, not the built-in default, reach the palette"). Keep them a palette that
 * is legible on its own — `ink` and `inkMuted` clear WCAG AAA, 7:1, against
 * every stop of the sky-to-ground gradient, which is asserted in that same test
 * file — because the case where they are used is the case where nobody chose.
 *
 * Typed as `ThemeColours` on purpose: the fallback has to be a complete instance
 * of the shape the schema declares, so a colour added to the theme block fails
 * here rather than resolving to `undefined` at the first frame.
 */
export const DEFAULT_PALETTE: ThemeColours = {
  sky: '#0f3057',
  ground: '#12352a',
  horizon: '#e8eef4',
  ink: '#ffffff',
  inkMuted: '#a8c4dc',
};

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** `#rrggbb` -> `0xrrggbb`. Phaser wants an integer everywhere but the clear colour. */
export function toPhaserColor(hex: string): number {
  return Number.parseInt(hex.slice(1), 16);
}

/**
 * `0xrrggbb` -> `#rrggbb`. The way back out, for the colours the *page* is
 * painted with: CSS custom properties are strings, and the land under the
 * horizon is a computed shade rather than a palette literal
 * (`GameRenderer.cssVariables`). Values outside 24 bits are masked rather than
 * throwing — a colour is not worth a failed boot.
 */
export function toCssColor(value: number): string {
  return `#${(Math.round(value) & 0xffffff).toString(16).padStart(6, '0')}`;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const invalid = (field: string, message: string) =>
  appErr('invalid', `config.boot.${field}`, message, { field });

/** A non-empty array of non-empty strings, or an `invalid` naming the field. */
function readStringArray(
  source: Record<string, unknown>,
  field: string,
): Result<readonly string[]> {
  const value = source[field];
  if (!Array.isArray(value) || value.length === 0) {
    return invalid(field, `"${field}" must be a non-empty array of strings.`);
  }
  const values = value.filter(
    (entry): entry is string => typeof entry === 'string' && entry.length > 0,
  );
  if (values.length !== value.length) {
    return invalid(field, `"${field}" must contain only non-empty strings.`);
  }
  return ok(values);
}

function readString(
  source: Record<string, unknown>,
  field: string,
): Result<string> {
  const value = source[field];
  if (typeof value !== 'string' || value.length === 0) {
    return invalid(field, `"${field}" must be a non-empty string.`);
  }
  return ok(value);
}

function readPositiveInt(
  source: Record<string, unknown>,
  field: string,
): Result<number> {
  const value = source[field];
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    return invalid(field, `"${field}" must be a positive integer.`);
  }
  return ok(value);
}

/**
 * The three graphics presets, validated shape by shape.
 *
 * These are what the renderer probe (task 1.19) chooses between, so a preset
 * with a missing or non-numeric member would reach the first frame as
 * `particles: undefined` and a level would emit `NaN` particles rather than
 * fail. Every member is checked, and the whole block is required: a config that
 * omits `graphicsPresets` has no tiers to degrade to, which is a build defect
 * and not something to paper over with defaults invented in the adapter.
 */
function readGraphicsPreset(
  raw: Record<string, unknown>,
  tier: string,
): Result<GraphicsPreset> {
  const path = `graphicsPresets.${tier}`;
  const source = raw[tier];
  if (!isRecord(source)) return invalid(path, `"${path}" must be an object.`);

  const number = (field: string, max?: number): Result<number> => {
    const value = source[field];
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
      return invalid(`${path}.${field}`, `"${path}.${field}" must be a positive number.`);
    }
    if (max !== undefined && value > max) {
      return invalid(`${path}.${field}`, `"${path}.${field}" must be at most ${max}.`);
    }
    return ok(value);
  };

  const renderScale = number('renderScale', 1);
  if (!renderScale.ok) return renderScale;
  const maxPixelRatio = number('maxPixelRatio');
  if (!maxPixelRatio.ok) return maxPixelRatio;

  const countable = (field: string): Result<number> => {
    const value = source[field];
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
      return invalid(
        `${path}.${field}`,
        `"${path}.${field}" must be an integer of zero or more.`,
      );
    }
    return ok(value);
  };

  const particles = countable('particles');
  if (!particles.ok) return particles;
  const parallaxLayers = countable('parallaxLayers');
  if (!parallaxLayers.ok) return parallaxLayers;

  const postProcessing = source['postProcessing'];
  if (typeof postProcessing !== 'boolean') {
    return invalid(`${path}.postProcessing`, `"${path}.postProcessing" must be a boolean.`);
  }

  return ok({
    renderScale: renderScale.value,
    maxPixelRatio: maxPixelRatio.value,
    particles: particles.value,
    parallaxLayers: parallaxLayers.value,
    postProcessing,
  });
}

function readGraphicsPresets(raw: Record<string, unknown>): Result<GraphicsPresets> {
  const presets = raw['graphicsPresets'];
  if (!isRecord(presets)) {
    return invalid('graphicsPresets', '"graphicsPresets" must be an object.');
  }

  const low = readGraphicsPreset(presets, 'low');
  if (!low.ok) return low;
  const medium = readGraphicsPreset(presets, 'medium');
  if (!medium.ok) return medium;
  const high = readGraphicsPreset(presets, 'high');
  if (!high.ok) return high;

  return ok({ low: low.value, medium: medium.value, high: high.value });
}

/**
 * `budgets.frameTimeMs` — the one budget the boot path needs.
 *
 * It is not a CI-only number: `visual-tier.ts` derives all five tier thresholds
 * from it, so it reaches the player as "how much scenery does this device get".
 * Read here rather than duplicated in the adapter, so the tier boundaries and
 * the CI gate can never be written against two different budgets.
 */
function readFrameTimeMs(raw: Record<string, unknown>): Result<number> {
  const budgets = raw['budgets'];
  if (!isRecord(budgets)) return invalid('budgets', '"budgets" must be an object.');
  const frameTimeMs = budgets['frameTimeMs'];
  if (typeof frameTimeMs !== 'number' || !Number.isFinite(frameTimeMs) || frameTimeMs <= 0) {
    return invalid(
      'budgets.frameTimeMs',
      '"budgets.frameTimeMs" must be a positive number of milliseconds.',
    );
  }
  return ok(frameTimeMs);
}

function readPalette(raw: Record<string, unknown>): Result<ThemeColours> {
  const theme = raw['theme'];
  if (theme === undefined) return ok(DEFAULT_PALETTE);
  if (!isRecord(theme)) return invalid('theme', '"theme" must be an object.');

  const merged: Record<keyof ThemeColours, string> = { ...DEFAULT_PALETTE };
  for (const key of Object.keys(DEFAULT_PALETTE) as (keyof ThemeColours)[]) {
    const value = theme[key];
    if (value === undefined) continue;
    if (typeof value !== 'string' || !HEX_COLOR.test(value)) {
      return invalid(`theme.${key}`, `"theme.${key}" must be a #rrggbb colour.`);
    }
    merged[key] = value;
  }
  return ok(merged);
}

/**
 * The one narrowing this parser cannot prove.
 *
 * `defaultLocale` is a branded `LocaleCode` in the port because the schema
 * constrains it to a supported locale — `common.schema.json#/$defs/locale`, an
 * enum of `en` and `fr` — and that constraint is checked by
 * `make validate-content`, not here: this parser only establishes "non-empty
 * string", which is the boot guard it has always been. A config that ships a
 * locale the game does not have fails CI, not the first frame.
 * Widening the field to `string` instead would silently drop it out of the
 * `Pick` above and hand back the freedom to misspell it, so the assertion is
 * kept, named, and confined to one line.
 */
const asLocaleCode = (value: string): GameConfigDocument['defaultLocale'] =>
  value as GameConfigDocument['defaultLocale'];

/**
 * Parse an untrusted `game.config.json` document into the renderer's view of it.
 * Every failure is an expected failure with a greppable code (`config.boot.*`).
 */
export function parseBootConfig(raw: unknown): Result<BootConfig> {
  if (!isRecord(raw)) {
    return invalid('document', 'game.config.json must be a JSON object.');
  }

  const title = readString(raw, 'title');
  if (!title.ok) return title;

  const version = readString(raw, 'version');
  if (!version.ok) return version;

  const defaultLocale = readString(raw, 'defaultLocale');
  if (!defaultLocale.ok) return defaultLocale;

  const designWidth = readPositiveInt(raw, 'designWidth');
  if (!designWidth.ok) return designWidth;

  const designHeight = readPositiveInt(raw, 'designHeight');
  if (!designHeight.ok) return designHeight;

  const flags = raw['featureFlags'];
  if (!isRecord(flags)) {
    return invalid('featureFlags', '"featureFlags" must be an object.');
  }
  const debugOverlay = flags['debugOverlay'];
  if (typeof debugOverlay !== 'boolean') {
    return invalid(
      'featureFlags.debugOverlay',
      '"featureFlags.debugOverlay" must be a boolean.',
    );
  }

  const palette = readPalette(raw);
  if (!palette.ok) return palette;

  /*
   * The locomotion modes a level document may name.
   *
   * From the config, never a literal in the engine (ADR-0023). `level-document.ts`
   * held the list as an array of eight strings, and Québec City arriving with
   * `toboggan` passed `make validate-content` — the schema knew the mode — and
   * then failed at load, because the parser did not. Two lists of the same thing
   * is one list too many, and the one in the engine is the one a content author
   * cannot see.
   */
  const locomotionModes = readStringArray(raw, 'locomotionModes');
  if (!locomotionModes.ok) return locomotionModes;

  const graphicsPresets = readGraphicsPresets(raw);
  if (!graphicsPresets.ok) return graphicsPresets;

  const frameTimeMs = readFrameTimeMs(raw);
  if (!frameTimeMs.ok) return frameTimeMs;

  return ok({
    title: title.value,
    version: version.value,
    defaultLocale: asLocaleCode(defaultLocale.value),
    designWidth: designWidth.value,
    designHeight: designHeight.value,
    backgroundColor: palette.value.sky,
    palette: palette.value,
    debugOverlay,
    locomotionModes: locomotionModes.value,
    graphicsPresets: graphicsPresets.value,
    frameTimeMs: frameTimeMs.value,
  });
}

/**
 * Linear per-channel blend between two `#rrggbb` colours, as a Phaser integer.
 *
 * The sky-to-ground gradient is drawn as horizontal bands rather than with
 * `Graphics.fillGradientStyle`, because that call is WebGL-only and would leave
 * a flat rectangle on the Canvas fallback path. Bands cost one fill each and the
 * whole background stays a single pass, which is what keeps the background's
 * share of the <= 4x overdraw budget at 1x (CLAUDE.md, Budgets).
 *
 * `position` is clamped to 0..1; a non-finite position resolves to `from`.
 */
export function mixColor(from: string, to: string, position: number): number {
  return blendColors(toPhaserColor(from), toPhaserColor(to), position);
}

/**
 * The same blend between two colours that are already Phaser integers.
 *
 * The land at the horizon is painted by mixing a shade of `palette.ground` back
 * into the sky-to-ground gradient it sits on, and both of those are computed
 * values rather than palette literals — so the mix has to take integers. Keeping
 * one implementation means the land and the sky cannot round differently and
 * leave a seam between them (`boot-scene.ts`, `#paintLand`).
 *
 * `position` is clamped to 0..1; a non-finite position resolves to `from`.
 */
export function blendColors(from: number, to: number, position: number): number {
  const t = Number.isFinite(position) ? Math.min(1, Math.max(0, position)) : 0;

  const channel = (shift: number): number => {
    const start = (from >> shift) & 0xff;
    const end = (to >> shift) & 0xff;
    return Math.round(start + (end - start) * t) & 0xff;
  };

  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
}
